import React, { useEffect, useMemo, useState } from 'react';
import { Upload, X } from 'lucide-react';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
const API_URL = `${BASE_URL}/api`;

const PLACEHOLDER = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
  `<svg xmlns="http://www.w3.org/2000/svg" width="320" height="240"><rect width="100%" height="100%" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#6b7280" font-family="Arial" font-size="14">No Image</text></svg>`
)}`;

const toAbsoluteUrl = (url) => {
  if (!url) return '';
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  // Accept both "/uploads/x" and "uploads/x"
  const cleaned = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  // If BASE_URL is empty, return root-relative URL (works in production)
  return `${BASE_URL}${cleaned}`;
};

export const ImageUpload = ({ onUploadComplete, token, existingImages = [] }) => {
  const safeExisting = useMemo(() => {
    if (!Array.isArray(existingImages)) return [];
    return existingImages
      .map((v) => (typeof v === 'string' ? v.trim() : ''))
      .filter(Boolean);
  }, [existingImages]);

  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState(safeExisting);

  // IMPORTANT: keep preview in sync when editing a product (existingImages arrives later)
  useEffect(() => {
    setUploadedImages(safeExisting);
  }, [safeExisting]);

  const tryUpload = async (file) => {
    const formData = new FormData();
    // Backend expects field name: "file"
    formData.append('file', file);

    const res = await axios.post(`${API_URL}/upload/image`, formData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      }
    });

    const urlFromApi = res?.data?.url;
    if (!urlFromApi) throw new Error('Upload succeeded but no URL returned');

    // Store RAW URL (usually "/uploads/...") so it stays valid even if BASE_URL changes.
    return String(urlFromApi).trim();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const newUrls = [];
      for (const file of files) {
        const url = await tryUpload(file);
        if (url) newUrls.push(url);
      }

      const allImages = [...uploadedImages, ...newUrls];
      setUploadedImages(allImages);
      onUploadComplete?.(allImages);
      toast.success('Images uploaded successfully');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || err?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(newImages);
    onUploadComplete?.(newImages);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-sm font-medium text-gray-700">Product Images</Label>
        <div className="mt-2">
          <label
            className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
              uploading
                ? 'border-gray-300 bg-gray-50 cursor-not-allowed'
                : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
            }`}
          >
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <Upload className={`w-8 h-8 mb-2 ${uploading ? 'text-gray-400' : 'text-gray-500'}`} />
              <p className="mb-2 text-sm text-gray-500">
                <span className="font-semibold">Click to upload images</span>
              </p>
              <p className="text-xs text-gray-500">JPEG, PNG, WebP (max 5MB each)</p>
            </div>
            <input
              type="file"
              className="hidden"
              accept="image/*"
              multiple
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {uploadedImages.length > 0 && (
        <div>
          <Label className="text-sm font-medium text-gray-700">Uploaded Images</Label>
          <div className="mt-2 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {uploadedImages.map((url, index) => (
              <div key={`${url}-${index}`} className="relative group">
                <img
                  src={toAbsoluteUrl(url) || PLACEHOLDER}
                  alt={`Product ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.src = PLACEHOLDER;
                  }}
                />
                <button
                  type="button"
                  onClick={() => removeImage(index)}
                  className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-3 h-3" />
                </button>
                {index === 0 && (
                  <span className="absolute bottom-1 left-1 bg-green-600 text-white text-xs px-2 py-1 rounded">
                    Primary
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {uploading && (
        <div className="flex items-center justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          <span className="ml-2 text-sm text-gray-600">Uploading...</span>
        </div>
      )}
    </div>
  );
};
